import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetDesignerBioQuery } from "./use-get-designer-bio";
import { useUserContext } from "./userContext";

type UseDesignerAvatarOptions = {
  autoLoad?: boolean;
};

export const useDesignerAvatar = (
  options?: UseDesignerAvatarOptions
) => {
  const autoLoad = options?.autoLoad ?? true;
  const { user } = useUserContext();
  const { getDesignerBio, designerBio } = useGetDesignerBioQuery();
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);

  const updateAvatarState = useCallback((url?: string) => {
    setAvatarUrl(url);
    if (typeof window === "undefined") return;

    if (url) {
      localStorage.setItem("designerAvatarUrl", url);
    } else {
      localStorage.removeItem("designerAvatarUrl");
    }

    window.dispatchEvent(
      new CustomEvent("designer-avatar-updated", { detail: url ?? null })
    );
  }, []);

  const initials = useMemo(() => {
    const first = user?.firstName?.trim()?.[0] ?? "";
    const last = user?.lastName?.trim()?.[0] ?? "";
    const combined = `${first}${last}`.trim();
    return combined.length ? combined.toUpperCase() : undefined;
  }, [user?.firstName, user?.lastName]);

  const refreshAvatar = useCallback(
    async (forceNetwork: boolean = true) => {
      if (user?.accountType !== "Designer" || !user?.id) {
        setAvatarUrl(undefined);
        return undefined;
      }

      try {
        const bio = await getDesignerBio(user.id, forceNetwork);
        const url = (bio?.image?.url as string | undefined) ?? undefined;
        updateAvatarState(url);
        return url;
      } catch (error) {
        console.error("Failed to refresh designer avatar:", error);
        return undefined;
      }
    },
    [user?.accountType, user?.id, getDesignerBio, updateAvatarState]
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedUrl = localStorage.getItem("designerAvatarUrl");
      if (storedUrl && storedUrl.trim() !== "") {
        setAvatarUrl(storedUrl);
      }

      const handleAvatarUpdated = (event: Event) => {
        const detail = (event as CustomEvent<string | null>).detail;
        setAvatarUrl(detail ?? undefined);
      };

      window.addEventListener(
        "designer-avatar-updated",
        handleAvatarUpdated as EventListener
      );

      return () => {
        window.removeEventListener(
          "designer-avatar-updated",
          handleAvatarUpdated as EventListener
        );
      };
    }
  }, []);

  useEffect(() => {
    if (user?.accountType !== "Designer" || !user?.id) {
      updateAvatarState(undefined);
      return;
    }

    const cachedUrl = designerBio?.image?.url as string | undefined;
    if (cachedUrl) {
      updateAvatarState(cachedUrl);
      return;
    }

    if (autoLoad) {
      refreshAvatar(true);
    }
  }, [
    autoLoad,
    designerBio?.image?.url,
    refreshAvatar,
    user?.accountType,
    user?.id,
    updateAvatarState,
  ]);

  return { avatarUrl, initials, refreshAvatar };
};
